import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProfileSectionImage from '../assets/profile-section-Image.png';
import { API_CONFIG } from '../config/api';
import LoadingScreen from '../components/LoadingScreen';
import { CloseIcon, ArrowLeftIcon } from '../components/Icons';

interface AssessmentResponse {
  questionId: string;
  question: string;
  answer: string | string[];
  category: 'General Physical Assessment' | 'IPAQ-SF' | 'PSQI';
}

interface PhysicalData {
  dateOfBirth?: string;
  age?: number;
  gender?: string;
  height?: number;
  heightCm?: number;
  heightFeet?: number;
  heightInches?: number;
  weight?: number;
  weightKg?: number;
  weightPounds?: number;
  chronicConditions?: boolean;
}

interface ExistingDemographics {
  hasExistingData: boolean;
  demographics: {
    dateOfBirth?: string;
    gender?: string;
    age?: number;
  } | null;
}

interface AssessmentData {
  responses: AssessmentResponse[];
}

const PhysicalWellnessLearnAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [answer, setAnswer] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [heightUnit, setHeightUnit] = useState('cm');
  const [heightValue, setHeightValue] = useState('');
  const [weightUnit, setWeightUnit] = useState('kg');
  const [weightValue, setWeightValue] = useState('');
  const [showFollowUpQuestion, setShowFollowUpQuestion] = useState(false);
  const [showAllergiesFollowUp, setShowAllergiesFollowUp] = useState(false);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [physicalData, setPhysicalData] = useState<PhysicalData>({});
  const [existingDemographics, setExistingDemographics] = useState<ExistingDemographics | null>(null);
  const [questionsToSkip, setQuestionsToSkip] = useState<number[]>([]);
  const [currentCategory, setCurrentCategory] = useState<'General Physical Assessment' | 'IPAQ-SF' | 'PSQI' | null>(null);
  const [showCategorySelection, setShowCategorySelection] = useState(true);

  // Auto-select assessment if coming from wellness card
  useEffect(() => {
    if (location.state?.directAssessment === 'General Physical Assessment' && showCategorySelection) {
      handleCategorySelect('General Physical Assessment');
    }
  }, [location.state]);

  // Define question categories
  const questionCategories = {
    'General Physical Assessment': {
      questions: [
        "What is your date of birth?",
        "What is your gender?",
        "What is your height?",
        "What is your weight?",
        "Do you have any chronic conditions?",
        "Are you a smoker? (Any type of tobacco product smoked daily or occasionally)",
        "We don't mean to pry, but do you have any medical history of high cholesterol, high blood pressure, heart disease, cancer, or diabetes in your family?",
        "Specifically, which medical condition? You may select more than one option.",
        "Are you currently seeing a doctor or undergoing any medical treatment?",
        "Please describe the treatment below.",
        "Do you have any allergies? (Food, medication, environmental, etc.)",
        "What specific allergies do you have? You may select more than one option.",
        "How much do you currently engage in physical activities?"
      ],
      totalQuestions: 13
    },
    'IPAQ-SF': {
      questions: [
        { id: 'vigorous-days', text: "During the last 7 days, on how many days did you do vigorous physical activities like heavy lifting, digging, aerobics, or fast bicycling?" },
        { id: 'vigorous-minutes', text: "How much time did you usually spend doing vigorous physical activities on one of those days?" },
        { id: 'moderate-days', text: "During the last 7 days, on how many days did you do moderate physical activities like bicycling at a regular pace, doubles tennis, or carrying light loads?" },
        { id: 'moderate-minutes', text: "How much time did you usually spend doing moderate physical activities on one of those days?" },
        { id: 'walking-days', text: "During the last 7 days, on how many days did you walk for at least 10 minutes at a time?" },
        { id: 'walking-minutes', text: "How much time did you usually spend walking on one of those days?" },
        { id: 'sitting-hours', text: "During the last 7 days, how much time did you spend sitting on a weekday?" }
      ],
      totalQuestions: 7
    },
    'PSQI': {
      questions: [
        { id: 'sleep-quality', text: "During the past month, how would you rate your sleep quality overall?" },
        { id: 'sleep-latency', text: "During the past month, how long has it usually taken you to fall asleep each night?" },
        { id: 'sleep-duration', text: "During the past month, how many hours of actual sleep did you get at night?" },
        { id: 'bedtime', text: "During the past month, what time have you usually gone to bed at night?" },
        { id: 'waketime', text: "During the past month, what time have you usually gotten up in the morning?" },
        { id: 'sleep-disturbances', text: "During the past month, how often have you had trouble sleeping?" },
        { id: 'sleep-medication', text: "During the past month, how often have you taken medicine to help you sleep?" },
        { id: 'daytime-dysfunction', text: "During the past month, how often have you had trouble staying awake while driving, eating meals, or engaging in social activity?" },
        { id: 'sleep-issues', text: "Frequency of issues like: waking during the night, bathroom use, breathing difficulty, coughing, feeling too hot/cold, nightmares, pain." },
        { id: 'enthusiasm', text: "During the past month, how much of a problem has it been for you to keep up enough enthusiasm to get things done?" }
      ],
      totalQuestions: 10
    }
  };

  const getTotalQuestionsForCategory = (category: string) => {
    return questionCategories[category as keyof typeof questionCategories]?.totalQuestions || 0;
  };


  const totalQuestions = currentCategory ? getTotalQuestionsForCategory(currentCategory) : 0;

  const handleCategorySelect = (category: 'General Physical Assessment' | 'IPAQ-SF' | 'PSQI') => {
    setCurrentCategory(category);
    setShowCategorySelection(false);
    setCurrentQuestion(1);
  };

  // Helper function to validate and format date input
  const validateAndFormatDate = (input: string): string => {
    // Remove any non-numeric characters except forward slashes
    const cleaned = input.replace(/[^\d/]/g, '');

    // If it's empty, return empty
    if (!cleaned) return '';

    // Auto-add slashes as user types
    let formatted = cleaned;
    if (cleaned.length >= 2 && !cleaned.includes('/')) {
      formatted = cleaned.substring(0, 2) + '/' + cleaned.substring(2);
    }
    if (cleaned.length >= 5 && formatted.split('/').length === 2) {
      const parts = formatted.split('/');
      if (parts[1].length >= 2) {
        formatted = parts[0] + '/' + parts[1].substring(0, 2) + '/' + parts[1].substring(2);
      }
    }

    return formatted;
  };

  // Initialize assessment data from localStorage or start fresh
  useEffect(() => {
    const initializeAssessment = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        // Clear any previous assessment data to start fresh
        localStorage.removeItem('physicalWellnessLearnAssessment');
        
        // Start with clean state for a new assessment
        setResponses([]);
        setPhysicalData({});
        setAssessmentId(null);
        setAnswer('');
        setSelectedOptions([]);

        // Demographics checking is not implemented in the current backend
        // We'll skip this functionality for now
        setExistingDemographics(null);

        setIsLoading(false);
      } catch (error) {

        setIsLoading(false);
      }
    };

    initializeAssessment();
  }, [navigate]);

  // Remove auto-population - start fresh for each retake
  useEffect(() => {
    // Keep fields cleared for new assessment
  }, [currentQuestion]);

  const saveCurrentResponse = () => {
    // Skip if this question should be skipped
    if (questionsToSkip.includes(currentQuestion)) {
      return;
    }

    // Use special question ID for follow-up questions
    let questionId = currentQuestion.toString();
    if (currentCategory === 'IPAQ-SF' || currentCategory === 'PSQI') {
      // For IPAQ-SF and PSQI, use the predefined question IDs
      const categoryQuestions = currentCategory ? questionCategories[currentCategory]?.questions || [] : [];
      const question = categoryQuestions[currentQuestion - 1] as { id: string; text: string } | undefined;
      questionId = question?.id || currentQuestion.toString();
    } else {
      if (showFollowUpQuestion && currentQuestion === 8) {
        questionId = "7.1"; // Medical conditions follow-up
      }
      if (showAllergiesFollowUp && currentQuestion === 12) {
        questionId = "11.1"; // Allergies follow-up
      }
    }

    // Determine the answer to save based on question type
    let answerToSave: string | string[] = '';
    if (currentQuestion === 3 && heightValue) {
      answerToSave = heightValue; // Save the height value
    } else if (currentQuestion === 4 && weightValue) {
      answerToSave = weightValue; // Save the weight value
    } else if (getAnswerOptions(currentQuestion)) {
      // Check if this question allows multiple selections based on questionId
      const allowsMultiple = questionId === "7.1" || questionId === "11.1";

      if (allowsMultiple) {
        answerToSave = selectedOptions; // Multiple selections - send array
      } else {
        answerToSave = selectedOptions.length > 0 ? selectedOptions[0] : ''; // Single selection - send first item as string
      }
    } else {
      answerToSave = answer;
    }

    // Final check: ensure single-selection questions send strings, not arrays
    let finalAnswer = answerToSave;
    if (Array.isArray(finalAnswer)) {
      // For categories that are single-selection, convert arrays to strings
      const questionCategory = getQuestionCategory(currentQuestion);
      if (questionCategory === 'PSQI' || questionCategory === 'IPAQ-SF' ||
          (questionCategory === 'General Physical Assessment' && questionId !== "7.1" && questionId !== "11.1")) {
        finalAnswer = finalAnswer.length > 0 ? finalAnswer[0] : '';
      }
    }

    const currentResponse: AssessmentResponse = {
      questionId: questionId,
      question: getQuestionText(currentQuestion),
      answer: finalAnswer,
      category: getQuestionCategory(currentQuestion)
    };

    // Update physical data for specific questions
    const updatedPhysicalData = { ...physicalData };
    if (currentQuestion === 1 && answer) {
      // Handle date of birth and calculate age
      // Convert MM/DD/YYYY to YYYY-MM-DD format for backend
      const dateParts = answer.split('/');
      if (dateParts.length === 3) {
        const month = dateParts[0].padStart(2, '0');
        const day = dateParts[1].padStart(2, '0');
        const year = dateParts[2];
        const isoDate = `${year}-${month}-${day}`;

        updatedPhysicalData.dateOfBirth = isoDate;
        if (isoDate) {
          const birthDate = new Date(isoDate);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          updatedPhysicalData.age = age;
        }
      }
    } else if (currentQuestion === 2 && selectedOptions.length > 0) {
      updatedPhysicalData.gender = selectedOptions[0].toLowerCase();
    } else if (currentQuestion === 5 && selectedOptions.length > 0) {
      // Handle chronic conditions - store as boolean or string based on selection
      updatedPhysicalData.chronicConditions = selectedOptions[0].toLowerCase() === 'yes';
    } else if (currentQuestion === 3 && heightValue && heightValue.trim()) {
      let value = parseFloat(heightValue);
      if (!isNaN(value) && value > 0) {
        if (heightUnit === 'cm') {
          updatedPhysicalData.heightCm = value;
          // Convert cm to feet (no inches stored per new rule)
          const totalInches = value / 2.54;
          updatedPhysicalData.heightFeet = Math.round((totalInches / 12) * 10) / 10;
          updatedPhysicalData.heightInches = 0;
        } else if (heightUnit === 'feet') {
          // Accept inputs like 5'10 -> convert to decimal feet
          const feetMatch = heightValue.match(/^(\d+)'(\d{1,2})?$/);
          if (feetMatch) {
            const f = parseInt(feetMatch[1], 10);
            const inches = feetMatch[2] ? parseInt(feetMatch[2], 10) : 0;
            value = Math.round(((f * 12 + inches) / 12) * 10) / 10;
          }
          updatedPhysicalData.heightFeet = value;
          updatedPhysicalData.heightInches = 0;
          // Convert feet to cm
          const totalInches = value * 12;
          updatedPhysicalData.heightCm = Math.round(totalInches * 2.54);
        }
      }
    } else if (currentQuestion === 4 && weightValue && weightValue.trim()) {
      const value = parseFloat(weightValue);
      if (!isNaN(value) && value > 0) {
        if (weightUnit === 'kg') {
          updatedPhysicalData.weight = value;
          updatedPhysicalData.weightKg = value;
          updatedPhysicalData.weightPounds = Math.round(value * 2.20462 * 10) / 10;
        } else if (weightUnit === 'pounds' || weightUnit === 'lb') {
          updatedPhysicalData.weight = Math.round(value / 2.20462 * 10) / 10;
          updatedPhysicalData.weightKg = Math.round(value / 2.20462 * 10) / 10;
          updatedPhysicalData.weightPounds = value;
        }
      }
    }

    // Update local responses
    const existingResponseIndex = responses.findIndex(r => r.questionId === currentResponse.questionId);
    let updatedResponses;
    if (existingResponseIndex >= 0) {
      updatedResponses = [...responses];
      updatedResponses[existingResponseIndex] = currentResponse;
    } else {
      updatedResponses = [...responses, currentResponse];
    }

    // Update state
    setResponses(updatedResponses);
    setPhysicalData(updatedPhysicalData);

    // Save to localStorage with different key for learn assessment
    const assessmentData = {
      responses: updatedResponses,
      physicalData: updatedPhysicalData,
      assessmentId: assessmentId
    };
    localStorage.setItem('physicalWellnessLearnAssessment', JSON.stringify(assessmentData));
  };

  const completeAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      setIsLoading(true);

      // Responses already have correct categories from saveCurrentResponse
      // Create the complete assessment data
      const completeAssessmentData = {
        physicalData: physicalData,
        assessmentData: {
          responses: responses
        },
        medicalConditions: [],
        familyHistory: [],
        medications: []
      };

      // Create new assessment using the correct endpoint and data format
      // Include physicalData for General Physical Assessment BMI calculations
      const assessmentData = {
        responses: completeAssessmentData.assessmentData.responses || [],
        physicalData: completeAssessmentData.physicalData || {}
      };

      const response = await fetch(API_CONFIG.ENDPOINTS.PHYSICAL_HEALTH, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(assessmentData)
      });

      if (!response.ok) {

        const errorText = await response.text();

        let errorMessage = 'Failed to save assessment data.';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (e) {
          // Keep default message if parsing fails
        }
        
        alert(errorMessage + ' Please try again.');
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      const assessmentId = data.data._id;
      
      console.log('Assessment created with ID:', assessmentId);

      // Complete the assessment using the correct endpoint with retry logic
      const completeAssessmentWithRetry = async (retryCount = 0) => {
        try {
          const completeResponse = await fetch(`${API_CONFIG.ENDPOINTS.PHYSICAL_HEALTH}/${assessmentId}/complete`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (completeResponse.ok) {
            // Get the completed assessment data
            const result = await completeResponse.json();

            // Clear localStorage after successful completion
            localStorage.removeItem('physicalWellnessLearnAssessment');


            // Navigate to the appropriate completion page based on category
            let completionRoute = '/panel/learn/physical-wellness-assessment/completion';
            if (currentCategory === 'General Physical Assessment') {
              completionRoute = '/panel/learn/general-physical-assessment/completion';
            } else if (currentCategory === 'IPAQ-SF') {
              completionRoute = '/panel/learn/ipaq-sf-assessment/completion';
            } else if (currentCategory === 'PSQI') {
              completionRoute = '/panel/learn/psqi-assessment/completion';
            }

            navigate(completionRoute, {
              state: { assessmentResult: result.data }
            });
            return;
          }

          // Handle 400 "already completed" error by fetching latest and navigating to completion
          if (completeResponse.status === 400) {
            const errorData = await completeResponse.json().catch(() => ({}));
            if (errorData.message && errorData.message.includes('already completed')) {
              // Fetch the latest assessment data and navigate to completion
              try {
                const latestResponse = await fetch(`${API_CONFIG.ENDPOINTS.PHYSICAL_HEALTH}/latest`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                  credentials: 'include',
                });

                if (latestResponse.ok) {
                  const latestData = await latestResponse.json();
                  if (latestData.success && latestData.data) {
                    // Clear localStorage after successful completion
                    localStorage.removeItem('physicalWellnessLearnAssessment');

                    // Navigate to the appropriate completion page based on category
                    let completionRoute = '/panel/learn/physical-wellness-assessment/completion';
                    if (currentCategory === 'General Physical Assessment') {
                      completionRoute = '/panel/learn/general-physical-assessment/completion';
                    } else if (currentCategory === 'IPAQ-SF') {
                      completionRoute = '/panel/learn/ipaq-sf-assessment/completion';
                    } else if (currentCategory === 'PSQI') {
                      completionRoute = '/panel/learn/psqi-assessment/completion';
                    }

                    navigate(completionRoute, {
                      state: { assessmentResult: latestData.data }
                    });
                    return;
                  }
                }
              } catch (fetchError) {

              }
            }
          }

          // Handle different error types
          let errorMessage = 'Failed to complete assessment';
          if (completeResponse.status === 429) {
            errorMessage = 'Too many requests. Please wait a moment before trying again.';
          } else if (completeResponse.status === 400) {
            const errorData = await completeResponse.json().catch(() => ({}));
            errorMessage = errorData.message || 'Invalid assessment data. Please try again.';
          } else if (completeResponse.status === 404) {
            errorMessage = 'Assessment not found. Please start over.';
          } else if (completeResponse.status >= 500) {
            errorMessage = 'Server error. Please try again in a few moments.';
          }

          // Retry logic for server errors and rate limiting
          if ((completeResponse.status >= 500 || completeResponse.status === 429) && retryCount < 2) {
            setTimeout(() => completeAssessmentWithRetry(retryCount + 1), 2000);
            return;
          }

          alert(errorMessage);

        } catch (error) {

          // Retry for network errors
          if (retryCount < 2) {
            setTimeout(() => completeAssessmentWithRetry(retryCount + 1), 2000);
            return;
          }

          alert('Network error. Please check your connection and try again.');
        }
      };

      await completeAssessmentWithRetry();
    } catch (error) {

    } finally {
      setIsLoading(false);
    }
  };

  const getQuestionCategory = (questionNumber: number): 'General Physical Assessment' | 'IPAQ-SF' | 'PSQI' => {
    // Return the current category (either 'General Physical Assessment', 'IPAQ-SF', or 'PSQI')
    return currentCategory || 'General Physical Assessment';
  };

  const handleNext = () => {
    // Save current response before moving to next question
    saveCurrentResponse();

    // Helper function to find next non-skipped question
    const findNextNonSkippedQuestion = (startFrom: number) => {
      let question = startFrom;
      while (question <= totalQuestions && questionsToSkip.includes(question)) {
        question++;
      }
      return question;
    };

    if (currentQuestion === 7 && (selectedOptions.includes("None") || selectedOptions.includes("I don't know"))) {
      // If "None" or "I don't know" is selected, skip question 8 and go to question 9
      setShowFollowUpQuestion(false);
      const nextQuestion = findNextNonSkippedQuestion(9);
      setCurrentQuestion(nextQuestion);
      setAnswer('');
      setSelectedOptions([]);
    } else if (currentQuestion === 7 && (selectedOptions.includes("Yes, my parents or siblings.") || selectedOptions.includes("Yes, I have been diagnosed"))) {
      // If "Yes" options are selected, show question 8
      setShowFollowUpQuestion(true);
      setCurrentQuestion(8);
      setSelectedOptions([]); // Clear for the follow-up question
    } else if (currentQuestion === 9 && selectedOptions.includes("No")) {
      // If "No" is selected in question 9, skip question 10 and go to question 11
      const nextQuestion = findNextNonSkippedQuestion(11);
      setCurrentQuestion(nextQuestion);
      setAnswer('');
      setSelectedOptions([]);
    } else if (currentQuestion === 11 && selectedOptions.includes("No")) {
      // If no allergies, skip to physical activity (Q13)
      const nextQuestion = findNextNonSkippedQuestion(13);
      setCurrentQuestion(nextQuestion);
      setAnswer('');
      setSelectedOptions([]);
    } else if (currentQuestion === 11 && selectedOptions.includes("Yes")) {
      // If has allergies, show follow-up question (Q12)
      setShowAllergiesFollowUp(true);
      setCurrentQuestion(12);
      setSelectedOptions([]);
    } else if (showFollowUpQuestion && currentQuestion === 8) {
      // After the follow-up question, continue to next regular question
      setShowFollowUpQuestion(false);
      const nextQuestion = findNextNonSkippedQuestion(9);
      setCurrentQuestion(nextQuestion);
      setAnswer('');
      setSelectedOptions([]);
    } else if (showAllergiesFollowUp && currentQuestion === 12) {
      // After the allergies follow-up question, continue to physical activity
      setShowAllergiesFollowUp(false);
      const nextQuestion = findNextNonSkippedQuestion(13);
      setCurrentQuestion(nextQuestion);
      setAnswer('');
      setSelectedOptions([]);
    } else if (currentQuestion < totalQuestions) {
      const nextQuestion = findNextNonSkippedQuestion(currentQuestion + 1);
      setCurrentQuestion(nextQuestion);
      setAnswer(''); // Clear answer for next question
      setSelectedOptions([]); // Clear selected options for next question
      setHeightUnit('cm'); // Reset height inputs
      setHeightValue('');
      setWeightUnit('kg'); // Reset weight inputs
      setWeightValue('');
    } else {
      // Complete the current category assessment and navigate to results
      completeAssessment();
    }
  };

  const handleBack = () => {
    // If we're currently on the category selection screen, go back to learn overview
    if (showCategorySelection) {
      navigate('/panel/learn/');
      return;
    }

    // If we're on the first question, return to the category selection UI
    if (currentQuestion === 1) {
      setShowCategorySelection(true);
      setCurrentCategory(null);
      setAnswer('');
      setSelectedOptions([]);
      setHeightUnit('cm');
      setHeightValue('');
      setWeightUnit('kg');
      setWeightValue('');
      return;
    }

    if (currentQuestion > 1) {
      // Helper function to find previous non-skipped question
      const findPreviousNonSkippedQuestion = (startFrom: number) => {
        let question = startFrom - 1;
        while (question >= 1 && questionsToSkip.includes(question)) {
          question--;
        }
        return question;
      };

      if (currentQuestion === 9) {
        // Check if we came from question 7 with "None" or "I don't know" (skipped question 8)
        const question7Response = responses.find(r => r.questionId === "7");
        if (question7Response && Array.isArray(question7Response.answer)) {
          const hasNoneOrDontKnow = question7Response.answer.includes("None") || question7Response.answer.includes("I don't know");
          if (hasNoneOrDontKnow) {
            // Go directly back to question 7
            setCurrentQuestion(7);
            setSelectedOptions(question7Response.answer as string[]);
            setAnswer('');
            return;
          }
        }
      }

      if (currentQuestion === 13) {
        // Check if we came from question 11 with "No" (skipped question 12)
        const question11Response = responses.find(r => r.questionId === "11");
        if (question11Response && Array.isArray(question11Response.answer)) {
          const hasNo = question11Response.answer.includes("No");
          if (hasNo) {
            // Go directly back to question 11
            setCurrentQuestion(11);
            setSelectedOptions(question11Response.answer as string[]);
            setAnswer('');
            return;
          }
        }
      }

      if (showFollowUpQuestion && currentQuestion === 8) {
        // Going back from follow-up question to question 7
        setShowFollowUpQuestion(false);
        setCurrentQuestion(7);
        // Load the original question 7 response
        const previousResponse = responses.find(r => r.questionId === "7");
        if (previousResponse && Array.isArray(previousResponse.answer)) {
          setSelectedOptions(previousResponse.answer as string[]);
        }

        // Clear follow-up question response
        const followUpResponse = responses.find(r => r.questionId === "7.1");
        if (followUpResponse) {
          // Remove the follow-up response since we're going back
          setResponses(prev => prev.filter(r => r.questionId !== "7.1"));
        }
        setAnswer('');
        return; // Exit early since we handled the response loading
      }

      if (showAllergiesFollowUp && currentQuestion === 12) {
        // Going back from allergies follow-up question to question 11
        setShowAllergiesFollowUp(false);
        setCurrentQuestion(11);
        // Load the original question 11 response
        const previousResponse = responses.find(r => r.questionId === "11");
        if (previousResponse && Array.isArray(previousResponse.answer)) {
          setSelectedOptions(previousResponse.answer as string[]);
        }

        // Clear allergies follow-up question response
        const allergiesFollowUpResponse = responses.find(r => r.questionId === "11.1");
        if (allergiesFollowUpResponse) {
          setResponses(prev => prev.filter(r => r.questionId !== "11.1"));
        }
        setAnswer('');
        return; // Exit early since we handled the response loading
      } else {
        const previousQuestion = findPreviousNonSkippedQuestion(currentQuestion);
        setCurrentQuestion(previousQuestion);

        // Load previous response if it exists
        const previousResponse = responses.find(r => r.questionId === previousQuestion.toString());
        if (previousResponse) {
          if (Array.isArray(previousResponse.answer)) {
            setSelectedOptions(previousResponse.answer as string[]);
            setAnswer('');
          } else {
            // Special handling for date of birth (question 1) - convert from ISO to MM/DD/YYYY
            if (previousQuestion === 1 && previousResponse.answer) {
              const date = new Date(previousResponse.answer as string);
              if (!isNaN(date.getTime())) {
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const day = date.getDate().toString().padStart(2, '0');
                const year = date.getFullYear();
                setAnswer(`${month}/${day}/${year}`);
              } else {
                setAnswer(previousResponse.answer as string);
              }
            } else {
              setAnswer(previousResponse.answer as string);
            }
            setSelectedOptions([]);
          }
        } else {
          setAnswer('');
          setSelectedOptions([]);
        }

        // Load height data for question 3
        if (previousQuestion === 3) {
          if (physicalData.heightCm && physicalData.heightCm > 0) {
            setHeightUnit('cm');
            setHeightValue(physicalData.heightCm.toString());
          } else if (physicalData.heightFeet && physicalData.heightFeet > 0) {
            setHeightUnit('feet');
            setHeightValue(physicalData.heightFeet.toString());
          } else {
            setHeightUnit('cm');
            setHeightValue('');
          }
        } else {
          setHeightUnit('cm');
          setHeightValue('');
        }

        // Load weight data for question 4
        if (previousQuestion === 4) {
          if (physicalData.weightKg && physicalData.weightKg > 0) {
            setWeightUnit('kg');
            setWeightValue(physicalData.weightKg.toString());
          } else if (physicalData.weightPounds && physicalData.weightPounds > 0) {
            setWeightUnit('lb');
            setWeightValue(physicalData.weightPounds.toString());
          } else {
            setWeightUnit('kg');
            setWeightValue('');
          }
        } else {
          setWeightUnit('kg');
          setWeightValue('');
        }
      }
    } else {
      navigate('/panel/learn/');
    }
  };

  const handleOptionSelect = (option: string) => {
    const currentOptions = getAnswerOptions(currentQuestion);
    if (currentOptions) {
      if (currentQuestion === 7) { // Question 7 has special mutual exclusivity logic
        if (option === "None") {
          // If "None" is selected, clear all other selections
          setSelectedOptions([option]);
        } else if (option === "I don't know") {
          // If "I don't know" is selected, clear all other selections
          setSelectedOptions([option]);
        } else {
          // For "Yes" options and "Others", clear "None" and "I don't know"
          const filteredOptions = selectedOptions.filter(opt =>
            opt !== "None" && opt !== "I don't know"
          );
          if (filteredOptions.includes(option)) {
            setSelectedOptions(filteredOptions.filter(opt => opt !== option));
          } else {
            setSelectedOptions([...filteredOptions, option]);
          }
        }
      } else if (currentQuestion === 8 || (showAllergiesFollowUp && currentQuestion === 12)) { // Question 8 and allergies follow-up (Q12) allow multiple selections
        if (selectedOptions.includes(option)) {
          setSelectedOptions(selectedOptions.filter(opt => opt !== option));
        } else {
          setSelectedOptions([...selectedOptions, option]);
        }
      } else {
        setSelectedOptions([option]);
      }
    }
  };

  const handleClose = () => {
    navigate('/panel/learn/');
  };

  const getQuestionText = (questionNumber: number) => {
    // If we're showing the follow-up question for medical conditions
    if (showFollowUpQuestion && questionNumber === 8) {
      return "Specifically, which medical condition? You may select more than one option.";
    }

    const categoryQuestions = currentCategory ? questionCategories[currentCategory]?.questions || [] : [];
    if (currentCategory === 'IPAQ-SF' || currentCategory === 'PSQI') {
      const question = categoryQuestions[questionNumber - 1] as { id: string; text: string } | undefined;
      return question?.text || "Question not found";
    } else {
      return categoryQuestions[questionNumber - 1] as string || "Question not found";
    }
  };

  const getAnswerOptions = (questionNumber: number) => {
    // Handle IPAQ-SF category - these need numeric inputs
    if (currentCategory === 'IPAQ-SF') {
      // IPAQ-SF questions don't have predefined options, they need numeric inputs
      return null; // Return null to indicate numeric input is needed
    }

    // Handle PSQI category - these have specific rating scales
    if (currentCategory === 'PSQI') {
      const psqiOptions = {
        1: [ // Sleep quality rating
          "Very good",
          "Fairly good",
          "Fairly bad",
          "Very bad"
        ],
        2: [ // Sleep latency (time to fall asleep)
          "≤15 min",
          "16–30 min",
          "31–60 min",
          ">60 min"
        ],
        3: [ // Sleep duration
          "≥7 hrs",
          "6–7 hrs",
          "5–6 hrs",
          "<5 hrs"
        ],
        6: [ // Sleep disturbances frequency
          "Not during the past month",
          "Less than once a week",
          "Once or twice a week",
          "Three or more times a week"
        ],
        7: [ // Use of sleeping medication
          "Not during the past month",
          "Less than once a week",
          "Once or twice a week",
          "Three or more times a week"
        ],
        8: [ // Daytime dysfunction
          "Not during the past month",
          "Less than once a week",
          "Once or twice a week",
          "Three or more times a week"
        ],
        9: [ // Sleep issues frequency
          "Not during the past month",
          "Less than once a week",
          "Once or twice a week",
          "Three or more times a week"
        ],
        10: [ // Enthusiasm to get things done
          "Not during the past month",
          "Less than once a week",
          "Once or twice a week",
          "Three or more times a week"
        ]
      };
      return psqiOptions[questionNumber as keyof typeof psqiOptions] || null;
    }

    // Handle General Physical Assessment category
    // If we're showing the follow-up question for medical conditions
    if (showFollowUpQuestion && questionNumber === 8) {
      return [
        "High cholesterol",
        "High blood pressure",
        "Heart disease or stroke",
        "Diabetes",
        "Cancer"
      ];
    }

    // If we're showing the follow-up question for allergies
    if (showAllergiesFollowUp && questionNumber === 12) {
      return [
        "Food allergies (nuts, dairy, gluten, etc.)",
        "Medication allergies (penicillin, aspirin, etc.)",
        "Environmental allergies (pollen, dust, pet dander)",
        "Insect sting allergies (bees, wasps, etc.)",
        "Contact allergies (latex, metals, chemicals)",
        "Seasonal allergies (hay fever, etc.)",
        "Others"
      ];
    }

    const options = {
      2: [
        "Male",
        "Female"
      ],
      5: [
        "Yes",
        "No"
      ],
      6: [
        "Yes, I am. Now or until recently.",
        "No, I quit more than 1 year ago.",
        "No, I have never smoked before."
      ],
      7: [
        "Yes, my parents or siblings.",
        "Yes, I have been diagnosed",
        "None",
        "I don't know"
      ],
      8: [
        "High cholesterol",
        "High blood pressure",
        "Heart disease or stroke",
        "Diabetes",
        "Cancer",
        "Others"
      ],
      9: [
        "Yes",
        "No"
      ],
      11: [
        "Yes",
        "No"
      ],
      13: [
        "More than 2 hours a week",
        "Less than 2 hours a week",
        "I don't know"
      ]
    };
    return options[questionNumber as keyof typeof options] || null;
  };

  const getQuestionInstruction = (questionNumber: number) => {
    if (currentCategory === 'IPAQ-SF') {
      const ipaqInstructions = [
        "Vigorous activities make you breathe much harder than normal. Count activities lasting at least 10 minutes.",
        "On days you did vigorous activities, how much total time did you spend?",
        "Moderate activities make you breathe somewhat harder than normal. Count activities lasting at least 10 minutes.",
        "On days you did moderate activities, how much total time did you spend?",
        "Walking means moving at your normal pace for at least 10 minutes at a time.",
        "On days you walked, how much total time did you spend walking?",
        "Include all time spent sitting or lying down while awake during a typical weekday."
      ];
      return ipaqInstructions[questionNumber - 1] || "Please provide your answer below.";
    }

    // General Physical Assessment instructions
    const instructions = [
      "Enter your date of birth in MM/DD/YYYY format. This information will be saved and won't be asked again in future assessments.",
      "Please select your gender identity.",
      "Please enter your height measurement.",
      "Please enter your weight measurement.",
      "Please select whether you have any chronic conditions.",
      "Please select your smoking status.",
      "Please select all that apply regarding your family medical history.",
      "Please select all that apply regarding your medical conditions.",
      "Please select whether you are currently seeing a doctor or undergoing treatment.",
      "Please describe your current treatment in detail.",
      "Please select whether you have any allergies.",
      "Please select all that apply regarding your allergies.",
      "Please select your current level of physical activity engagement."
    ];
    return instructions[questionNumber - 1] || "Please provide your answer below.";
  };

  const getPlaceholderText = (questionNumber: number) => {
    const placeholders = [
      "Please key in your value here",
      "Please select your gender",
      "Please key in your height",
      "Please key in your weight",
      "Please select your answer",
      "Please select your answer",
      "Please select your answer",
      "Please select your answer",
      "Please select your answer",
      "Please describe your treatment here",
      "Please select your answer"
    ];
    return placeholders[questionNumber - 1] || "Please enter your answer";
  };

  const progressPercentage = (currentQuestion / totalQuestions) * 100;

  // Show loading state while initializing
  if (isLoading) {
    return <LoadingScreen message="Loading assessment..." fullScreen />;
  }

  return (
    <div className="assessment-form-page">
      <div className="assessment-container">
        {/* Left Panel - Background Image */}
        <div
          className="assessment-left-panel physical-panel"
        >
          <div className="assessment-header">
            <h1>Physical Wellness Assessment</h1>
            {currentCategory && currentCategory !== 'PSQI' && currentCategory !== 'IPAQ-SF' && currentCategory !== 'General Physical Assessment' && (
              <div className="category-indicator">
                <span className="category-label">Current Section:</span>
                <span className="category-name">{currentCategory}</span>
              </div>
            )}
            <p className="assessment-subtitle">
              Comprehensive Health Evaluation
            </p>
          </div>
        </div>

        {/* Right Panel - Category Selection or Questions */}
        <div className="assessment-right-panel">
          {showCategorySelection ? (
            /* Category Selection */
            <>
              <div className="assessment-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: '0%' }}></div>
                </div>
                <span className="progress-text">
                  Select Assessment Type
                </span>
              </div>

              <div className="question-section">
                <h2 className="question-title">
                  Choose Your Physical Assessment
                </h2>

                <p className="question-instruction">
                  Choose which physical assessment you'd like to complete. Each assessment focuses on different aspects of your physical health.
                </p>

                <div className="selection-options">
                  <button
                    className="option-button"
                    onClick={() => handleCategorySelect('General Physical Assessment')}
                  >
                    <div style={{ textAlign: 'left' }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
                        General Physical Assessment
                      </h3>
                      <p style={{ margin: '0', fontSize: '0.9rem', color: '#64748b', lineHeight: '1.4' }}>
                        Comprehensive evaluation of your physical health including medical history, lifestyle habits, and general wellness indicators.
                      </p>
                    </div>
                  </button>

                  <button
                    className="option-button"
                    onClick={() => handleCategorySelect('IPAQ-SF')}
                  >
                    <div style={{ textAlign: 'left' }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
                        IPAQ-SF Physical Activity Assessment
                      </h3>
                      <p style={{ margin: '0', fontSize: '0.9rem', color: '#64748b', lineHeight: '1.4' }}>
                        Scientifically validated questionnaire measuring your physical activity levels using MET (Metabolic Equivalent) calculations.
                      </p>
                    </div>
                  </button>

                  <button
                    className="option-button"
                    onClick={() => handleCategorySelect('PSQI')}
                  >
                    <div style={{ textAlign: 'left' }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
                        PSQI Sleep Quality Assessment
                      </h3>
                      <p style={{ margin: '0', fontSize: '0.9rem', color: '#64748b', lineHeight: '1.4' }}>
                        Pittsburgh Sleep Quality Index measuring your sleep quality and patterns over the past month.
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* Questions */
            <>
              <div className="assessment-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progressPercentage}%` }}></div>
                </div>
                <span className="progress-text">
                  Question {currentQuestion} of {totalQuestions}
                </span>
              </div>

          <div className="question-section">
            {getQuestionCategory(currentQuestion) === 'IPAQ-SF' && (
              <div className="category-indicator">
                <span className="category-badge">
                  IPAQ-SF Physical Activity Assessment
                </span>
              </div>
            )}
            {getQuestionCategory(currentQuestion) === 'PSQI' && (
              <div className="category-indicator">
                <span className="category-badge">
                  PSQI Sleep Quality Assessment
                </span>
              </div>
            )}

            <h2 className="question-title">
              {getQuestionText(currentQuestion)}
            </h2>

            {currentCategory !== 'PSQI' && (
              <p className="question-instruction">
                {getQuestionInstruction(currentQuestion)}
              </p>
            )}

            {currentCategory === 'General Physical Assessment' && currentQuestion === 1 ? (
              <div className="answer-input-container">
                <input
                  type="text"
                  value={answer}
                  onChange={(e) => setAnswer(validateAndFormatDate(e.target.value))}
                  placeholder="MM/DD/YYYY (e.g., 10/12/1998)"
                  className="answer-input"
                  maxLength={10}
                />
              </div>
            ) : currentCategory === 'PSQI' ? (
              <div className="answer-input-container">
                {currentQuestion === 4 || currentQuestion === 5 ? (
                  // Time inputs for bedtime and waketime (questions 4 and 5)
                  <input
                    type="time"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="HH:MM"
                    className="answer-input"
                  />
                ) : (
                  // Regular select options for other PSQI questions
                  <div className="answer-options">
                    {getAnswerOptions(currentQuestion)?.map((option, index) => (
                      <label key={index} className="answer-option">
                        <input
                          type="radio"
                          name={`question-${currentQuestion}`}
                          value={index}
                          checked={selectedOptions.includes(option)}
                          onChange={() => handleOptionSelect(option)}
                        />
                        <span className="option-text">{option}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ) : currentCategory === 'IPAQ-SF' ? (
              <div className="answer-input-container">
                {currentQuestion === 1 || currentQuestion === 3 || currentQuestion === 5 ? (
                  // Days input (questions 1, 3, 5)
                  <input
                    type="number"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Enter number of days (0-7)"
                    className="answer-input"
                    min="0"
                    max="7"
                    autoComplete="off"
                  />
                ) : currentQuestion === 2 || currentQuestion === 4 || currentQuestion === 6 ? (
                  // Time input (questions 2, 4, 6) - minutes
                  <input
                    type="number"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Enter minutes per day (e.g., 30)"
                    className="answer-input"
                    min="0"
                    max="1440"
                    autoComplete="off"
                  />
                ) : currentQuestion === 7 ? (
                  // Sitting time input (question 7) - hours
                  <input
                    type="number"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Enter hours per weekday (e.g., 8)"
                    className="answer-input"
                    min="0"
                    max="24"
                    step="0.5"
                    autoComplete="off"
                  />
                ) : (
                  <input
                    type="text"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Enter your answer"
                    className="answer-input"
                  />
                )}
              </div>
            ) : getAnswerOptions(currentQuestion) ? (
              <div className="answer-options">
                {getAnswerOptions(currentQuestion)?.map((option, index) => (
                  <label key={index} className={`answer-option ${selectedOptions.includes(option) ? 'selected' : ''}`}>
                    <input
                      type={currentQuestion === 8 || (showAllergiesFollowUp && currentQuestion === 12) ? "checkbox" : "radio"}
                      name={currentQuestion === 8 || (showAllergiesFollowUp && currentQuestion === 12) ? `question-${currentQuestion}` : `question-${currentQuestion}-single`}
                      checked={selectedOptions.includes(option)}
                      onChange={() => handleOptionSelect(option)}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
            ) : currentQuestion === 3 ? (
              <div className="answer-input-container">
                <div className="height-input-group">
                  <select
                    value={heightUnit}
                    onChange={(e) => setHeightUnit(e.target.value)}
                    className="unit-select"
                  >
                    <option value="cm">CM</option>
                    <option value="feet">Feet</option>
                  </select>
                  <input
                    type={heightUnit === 'feet' ? 'text' : 'number'}
                    value={heightValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (heightUnit === 'feet') {
                        // allow digits and apostrophe, auto-insert ' after first digit when typing two or more digits with no '
                        let cleaned = v.replace(/[^0-9']/g, '');
                        if (!cleaned.includes("'") && /^\d{2,}$/.test(cleaned)) {
                          cleaned = cleaned.slice(0, 1) + "'" + cleaned.slice(1);
                        }
                        setHeightValue(cleaned);
                      } else {
                        setHeightValue(v);
                      }
                    }}
                    placeholder={heightUnit === 'cm' ? 'Enter height in cm (e.g., 170)' : "Enter height in feet (e.g., 5'10)"}
                    className="height-input"
                    min={heightUnit === 'cm' ? '0' : undefined}
                    step={heightUnit === 'cm' ? '0.1' : undefined}
                    autoComplete="off"
                    data-form-type="height"
                  />
                </div>
              </div>
            ) : currentQuestion === 4 ? (
              <div className="answer-input-container">
                <div className="weight-input-group">
                  <select
                    value={weightUnit}
                    onChange={(e) => setWeightUnit(e.target.value)}
                    className="unit-select"
                  >
                    <option value="kg">KG</option>
                    <option value="lb">LB</option>
                  </select>
                  <input
                    type="number"
                    value={weightValue}
                    onChange={(e) => setWeightValue(e.target.value)}
                    placeholder={weightUnit === 'kg' ? 'Enter weight in kg (e.g., 70)' : 'Enter weight in lb (e.g., 150)'}
                    className="weight-input"
                    min="0"
                    step="0.1"
                    autoComplete="off"
                    data-form-type="weight"
                  />
                </div>
              </div>
            ) : (
              <div className="answer-input-container">
                {currentQuestion === 10 ? (
                  <textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder={getPlaceholderText(currentQuestion)}
                    className="answer-textarea"
                    rows={4}
                  />
                ) : (
                  <input
                    type="text"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder={getPlaceholderText(currentQuestion)}
                    className="answer-input"
                  />
                )}
              </div>
            )}
            </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={showCategorySelection}
            >
              Back
            </button>

            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={
                getAnswerOptions(currentQuestion)
                  ? selectedOptions.length === 0
                  : currentCategory === 'General Physical Assessment' && currentQuestion === 3
                    ? !heightValue.trim()
                    : currentCategory === 'General Physical Assessment' && currentQuestion === 4
                      ? !weightValue.trim()
                      : !answer.trim()
              }
            >
              {currentQuestion === totalQuestions ? 'Complete Assessment' : 'Next'}
            </button>
          </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default PhysicalWellnessLearnAssessmentPage;


